import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { applications } from '../api';
import { useAuth } from '../contexts/AuthContext';
import DataTable, { Column } from '../components/DataTable';
import type { Application } from '../types';

/**
 * Applications: was ausgerollt wird, getrennt von dem, was verkauft wird.
 *
 * Welche Software es gibt, welche Produkte sie verkaufen und was an ihr
 * hängt. Seit Stufe 2 (10.10.2026) werden Vorlagen, Felder und Releases an der
 * Application gepflegt (Detailseite). Siehe docs/KONZEPT-PRODUKT-UND-APPVORLAGE.md.
 */
export default function ApplicationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Dieselben Rollen wie im Backend (POST /api/applications)
  const darfAnlegen = user?.role === 'ADMIN' || user?.role === 'PRODUCT_MANAGER';
  const [anlegen, setAnlegen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['applications'], queryFn: applications.list });

  const zahl = (n?: number) => <span className="text-xs tabular-nums text-slate-500">{n ?? '—'}</span>;

  const columns: Column<Application>[] = [
    {
      key: 'name',
      label: 'Name',
      render: (r) => (
        <div>
          <button onClick={() => navigate(`/applications/${r.id}`)} className="font-medium text-slate-800 hover:text-brand-700">{r.name}</button>
          {r.description && <div className="max-w-md truncate text-xs text-slate-500">{r.description}</div>}
        </div>
      ),
    },
    { key: 'slug', label: 'Slug', render: (r) => <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{r.slug}</code> },
    {
      key: 'products',
      label: 'Verkauft als',
      render: (r) =>
        r.products.length === 0 ? (
          <span className="text-xs text-slate-400">kein Produkt</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {r.products.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/products/${p.id}`)}
                title={p.imVerkauf ? 'Im Verkauf' : 'Nicht im Verkauf — nur Betrieb'}
                className={
                  p.imVerkauf
                    ? 'rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs text-brand-700 hover:bg-brand-100'
                    : 'rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-500 hover:bg-slate-50'
                }
              >
                {p.name}
                {!p.imVerkauf && <span className="ml-1 text-slate-400">· Betrieb</span>}
              </button>
            ))}
          </div>
        ),
    },
    { key: 'templates', label: 'Vorlagen', render: (r) => zahl(r._count?.templates) },
    { key: 'configFields', label: 'Felder', render: (r) => zahl(r._count?.configFields) },
    { key: 'releases', label: 'Releases', render: (r) => zahl(r._count?.releases) },
    { key: 'instances', label: 'Instanzen', render: (r) => zahl(r._count?.instances) },
  ];

  return (
    <div className="space-y-4">
      {darfAnlegen && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setAnlegen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            <Plus size={15} /> Application anlegen
          </button>
        </div>
      )}
      {anlegen && <ApplicationAnlegen onClose={() => setAnlegen(false)} onFertig={(id) => navigate(`/applications/${id}`)} />}
      <p className="max-w-3xl text-sm text-slate-500">
        Eine Application ist die Software, die ausgerollt wird — mit ihren Vorlagen, Einstellungen und Releases. Ein Produkt
        ist das Angebot, das sie verkauft. Mehrere Produkte können dieselbe Application verkaufen; eine Application für den
        reinen Betrieb braucht gar keins. Vorlagen, Einstellungen und Releases werden hier gepflegt, an der Application.
      </p>
      <DataTable
        columns={columns}
        data={data ?? []}
        loading={isLoading}
        emptyMessage="Noch keine Applications. Sie entstehen beim nächsten Start des PC aus den vorhandenen Produkten."
        rowKey={(r) => r.id}
      />
    </div>
  );
}

/**
 * Neue Application von Hand. Danach geht es auf ihre Seite: dort kommen
 * Vorlage, Felder und Releases dazu, und unter Produkte lässt sie sich
 * verkaufen. Der Slug ist danach fest — er steht in Seeds, Skripten und
 * Stack-Namen (kürzel-umgebung-slug).
 */
function ApplicationAnlegen({ onClose, onFertig }: { onClose: () => void; onFertig: (id: string) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugVonHand, setSlugVonHand] = useState(false);
  const [beschreibung, setBeschreibung] = useState('');
  const [fehler, setFehler] = useState('');
  const ausName = (n: string) =>
    n.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const slugGueltig = /^[a-z0-9-]+$/.test(slug);
  const speichern = useMutation({
    mutationFn: () => applications.create({ slug, name: name.trim(), ...(beschreibung.trim() ? { description: beschreibung.trim() } : {}) }),
    onSuccess: (a) => { qc.invalidateQueries({ queryKey: ['applications'] }); onFertig(a.id); },
    onError: (e: unknown) =>
      setFehler((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Anlegen fehlgeschlagen.'),
  });
  const feld = 'w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form
        className="w-full max-w-md rounded-xl bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); setFehler(''); speichern.mutate(); }}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">Application anlegen</h2>
          <button type="button" onClick={onClose} aria-label="Schließen" className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="space-y-3 px-6 py-4">
          {fehler && <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{fehler}</p>}
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Name *</span>
            <input
              value={name}
              autoFocus
              onChange={(e) => { setName(e.target.value); if (!slugVonHand) setSlug(ausName(e.target.value)); }}
              placeholder="z. B. Moodle"
              className={feld}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Slug *</span>
            <input
              value={slug}
              onChange={(e) => { setSlugVonHand(true); setSlug(e.target.value); }}
              placeholder="moodle"
              className={`${feld} font-mono ${slug && !slugGueltig ? 'border-amber-400 bg-amber-50' : ''}`}
            />
            <span className="mt-1 block text-[11px] text-slate-400">
              Kleinbuchstaben, Ziffern, Bindestriche. Steht später in Stack-Namen und lässt sich nicht mehr ändern.
            </span>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Beschreibung</span>
            <textarea value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} rows={3} className={feld} />
          </label>
          <p className="text-xs text-slate-500">
            Danach auf der Seite der Application eine Vorlage anlegen; verkauft wird sie über ein Produkt.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700">Abbrechen</button>
          <button
            type="submit"
            disabled={!name.trim() || !slugGueltig || speichern.isPending}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {speichern.isPending ? 'Legt an …' : 'Anlegen'}
          </button>
        </div>
      </form>
    </div>
  );
}
